<template>
	<EditorShell>
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template.name"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-px-4 s-max-w-4xl s-mx-auto">
			<!-- Preview switcher -->
			<EditorPreviewSwitcher
				class="s-mt-4 s-h-10"
				:showPreview="showPreview"
				@preview:toggle="showPreview = !showPreview"
				@preview:open="renderPreview"
				@render:click="renderPreview"
				v-model="previewVariables"
			/>

			<JFlex>
				<!-- Input and Output Fields -->
				<JBox class="s-w-6/12" :class="{ 's-w-full': !showPreview }">
					<ExcelInputFields :id="id" :templateInputs="template.inputs" :excelInputs="excelInputs" :inputPrefix="inputPrefix" />
					<ExcelOutputFields
						class="s-mt-4"
						:id="id"
						:templateOutputs="template.outputs"
						:excelOutputs="excelOutputs"
						:outputPrefix="outputPrefix"
					/>
				</JBox>
				<!-- Preview -->
				<RenderPreview v-if="showPreview" class="s-w-6/12 s-ml-4" :values="previewResult">
					<JText v-if="hasChanged" class="s-text-sm s-bg-gray-100 s-p-3 s-rounded-lg s-mt-4"
						>There are changes in the template. You need to Save them first to reflect the changes in the preview.</JText
					>
					<JSpinner type="dots" :delay="0" v-if="isPreviewLoading" />
				</RenderPreview>
			</JFlex>

			<!-- Excel fileKey -->
			<EditorAccordion :headline="$t('templates.excelFile')" class="s-my-4">
				<JFlex class="s-justify-end s-items-center s-mb-2">
					<JButton variant="default-light-sm" @click="toggleFilePicker">{{ $t("templates.selectOrUploadFile") }}</JButton>
				</JFlex>
				<JInputText v-model="template.fileKey" class="s-mb-4" :disabled="true" />
			</EditorAccordion>

			<!-- Graphql Queries -->
			<EditorAccordion :headline="$t('core.graphQlQuery')" class="s-mb-4">
				<CGraphQlQueryLibrary v-model="template.graphQlQuery" />
			</EditorAccordion>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template" />
			</EditorAccordion>

			<!-- Delete template -->
			<CDialogProvider
				class="s-pb-4"
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>
		</JBox>

		<!-- File Picker-->
		<CModal variant="default-large" :isVisible="showFilePicker" @overlay:click="toggleFilePicker">
			<CFilesExplorer :isFilePicker="true" @file:selected="handleFileSelect" />
		</CModal>

		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorAccordion from "../components/EditorAccordion"
import EditorDebugConsole from "../components/EditorDebugConsole"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import ExcelInputFields from "../components/function/ExcelInputFields"
import ExcelOutputFields from "../components/function/ExcelOutputFields"
import RenderPreview from "../components/function/RenderPreview"
import api from "@/api/api"
import { query, mutation } from "../api/queries"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import axios from "axios"
import XLSX from "xlsx"
import { getDataSchemaFromXlsx } from "../utils/parseExcelUtils.js"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"

export default {
	components: {
		EditorShell,
		EditorPreviewSwitcher,
		EditorAccordion,
		EditorDebugConsole,
		EditorSaveChangesPrompt,
		EditorDocumentSettings,
		ExcelInputFields,
		ExcelOutputFields,
		RenderPreview
	},

	props: {
		id: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			isLoading: false,
			template: {},
			originalTemplate: {},

			showFilePicker: false,

			// Excel inputs and outputs
			inputPrefix: "input_",
			outputPrefix: "output_",
			excelInputs: [],
			excelOutputs: [],

			// Preview
			showPreview: false,
			isPreviewLoading: false,
			previewResult: null,
			previewVariables: null, // user can define what variables to pass
			previewData: null, // cached data fetched from the API to be passed to renderPreview

			// Errors for debug console
			errors: []
		}
	},

	methods: {
		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors() // reset

			const variables = { where: { id: this.id } }
			const res = await api({ query: query.fetchFunctionTemplate, variables })

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(this.template)

			// Parse inputs and outpus
			this.parseInputsAndOutputsFromExcel()
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors() // reset

			this.isLoading = true
			const res = await api({
				query: mutation.updateFunctionTemplate,
				variables: {
					id: this.id,
					input: {
						name: this.template.name,
						description: this.template.description,
						fileKey: this.template.fileKey,
						graphQlQuery: this.template.graphQlQuery
					}
				}
			})
			this.isLoading = false

			if (res?.data?.updateFunctionTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateFunctionTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template) // update also original so that comparison works

			// Refetch
			//In case we change the file, we need to refetch so that we get the result back from parsed excel
			this.fetchTemplate()
		},

		toggleFilePicker() {
			this.showFilePicker = !this.showFilePicker
		},

		getFileExtension(str) {
			var file = str.split("/").pop()
			var fileAndExtensionArray = file.split(".")
			return fileAndExtensionArray?.pop()
		},

		handleFileSelect(payload) {
			const fileExtension = this.getFileExtension(payload.path)
			if (fileExtension !== "xlsx") {
				this.setErrors([this.$t("templates.pleaseSelectExcelFile")])
				return
			}

			this.toggleFilePicker()
			this.template.fileKey = payload?.path
		},

		async parseInputsAndOutputsFromExcel() {
			if (!this.template.fileKey) return

			// TODO: refactor, url should come from env
			const res = await axios({
				url: `https://api.covergo.com/api/v1/files/${encodeURIComponent(this.template.fileKey)}`,
				headers: {
					Authorization: `Bearer ${this.$store.state.auth.token}`
				},
				method: "GET",
				responseType: "blob" // important
			})
			const reader = new FileReader()
			reader.readAsArrayBuffer(res.data)

			reader.onload = () => {
				const data = new Uint8Array(reader.result)
				let workBookData = XLSX.read(data, { type: "array" })
				this.excelInputs = getDataSchemaFromXlsx({
					wb: workBookData,
					prefix: this.inputPrefix
				})
				this.excelOutputs = getDataSchemaFromXlsx({
					wb: workBookData,
					prefix: this.outputPrefix
				})
			}
		},

		async deleteTemplate() {
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id }
			})

			if (res.data?.deleteTemplate?.status !== "success") {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: res?.errors?.[0] || res?.data?.deleteTemplate?.errors?.[0] }
				})
			}
			this.$router.push({ name: "ViewTemplatesHome" })
		},

		async renderPreview() {
			if (this.isPreviewLoading) return
			this.setErrors() // reset

			this.isPreviewLoading = true
			const res = await api({
				query: mutation.renderFunctionTemplate,
				variables: {
					templateId: this.id,
					input: {
						name: "data",
						variablesJsonString: this.previewVariables ? this.previewVariables : "{}"
					}
				}
			})
			this.isPreviewLoading = false

			// Check errors
			if (res?.data?.renderFunctionTemplate?.status !== "success") {
				this.setErrors(res?.errors?.map(item => item?.message) || res?.data?.renderFunctionTemplate?.errors)
				this.previewResult = null
				return
			}

			this.previewResult = res?.data?.renderFunctionTemplate?.value
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		}
	},

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		}
	},

	mounted() {
		this.fetchTemplate()
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, showDialog: this.hasChanged })
	}
}
</script>
